import {
  CUSTOM_ORG_ROLE_PERMISSIONS,
  type CreateOrgRoleRequest,
  type CreateOrgRoleResponse,
  type CustomOrgRolePermission,
  type DeleteOrgRoleResponse,
  type ErrorCode,
  type ListOrgRolesResponse,
  type OrgRoleView,
  type TenantOrgPermission,
  type UpdateOrgRoleRequest,
  type UpdateOrgRoleResponse,
} from '@nadoutong/contracts';
import { platformApiRequest } from './client.ts';

/**
 * Typed wrappers for `GET/POST/PATCH/DELETE /v1/org/roles` (tenant #178).
 *
 * Wire types and the closed `org:*` list come from `@nadoutong/contracts`
 * (router #2312). This module is the fetch layer. A 404/error is still a
 * supported degraded mode — environments may deploy the backend later.
 *
 * Path is session-scoped (`/v1/org/roles`), not `/v1/orgs/:orgId/roles`.
 */

export {
  CUSTOM_ORG_ROLE_PERMISSIONS,
  type CreateOrgRoleRequest,
  type CreateOrgRoleResponse,
  type CustomOrgRolePermission,
  type DeleteOrgRoleResponse,
  type ListOrgRolesResponse,
  type OrgRoleView,
  type UpdateOrgRoleRequest,
  type UpdateOrgRoleResponse,
};

export const ORG_ROLE_NAME_MAX_LENGTH = 80;

export const ORG_ROLE_NOT_FOUND = 'ORG_ROLE_NOT_FOUND' satisfies ErrorCode;
export const ORG_ROLE_NAME_TAKEN = 'ORG_ROLE_NAME_TAKEN' satisfies ErrorCode;
export const ORG_ROLE_IN_USE = 'ORG_ROLE_IN_USE' satisfies ErrorCode;

export function isCustomOrgRolePermission(
  value: TenantOrgPermission,
): value is CustomOrgRolePermission {
  return CUSTOM_ORG_ROLE_PERMISSIONS.some((item) => item === value);
}

export async function listOrgRoles(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListOrgRolesResponse> {
  return platformApiRequest<ListOrgRolesResponse>('/v1/org/roles', { signal }, fetchImpl);
}

export async function createOrgRole(
  body: CreateOrgRoleRequest,
  fetchImpl: typeof fetch,
): Promise<CreateOrgRoleResponse> {
  return platformApiRequest<CreateOrgRoleResponse>(
    '/v1/org/roles',
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function updateOrgRole(
  roleId: string,
  body: UpdateOrgRoleRequest,
  fetchImpl: typeof fetch,
): Promise<UpdateOrgRoleResponse> {
  return platformApiRequest<UpdateOrgRoleResponse>(
    `/v1/org/roles/${encodeURIComponent(roleId)}`,
    { method: 'PATCH', body },
    fetchImpl,
  );
}

export async function deleteOrgRole(
  roleId: string,
  fetchImpl: typeof fetch,
): Promise<DeleteOrgRoleResponse> {
  return platformApiRequest<DeleteOrgRoleResponse>(
    `/v1/org/roles/${encodeURIComponent(roleId)}`,
    { method: 'DELETE' },
    fetchImpl,
  );
}
